import React, { useEffect, useState } from 'react';
import { Button, InputGroup, FormControl, Form } from 'react-bootstrap';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { useOriginSearch } from './useOriginSearch';
import { useLocation } from 'react-router-dom';
import '../../styles/components/common/SearchBar.css';
import PropTypes from 'prop-types';

const SearchBar = ({ navigate, variant = 'landing', onSelectSuggestion = null }) => {
  const {
    searchQuery,
    suggestions,
    isLoading,
    error,
    searchInputRef,
    dropdownRef,
    handleSearchChange,
    handleSuggestionClick,
    handleClickOutside,
    handleKeyPress,
    handleFocus,
  } = useOriginSearch(navigate, onSelectSuggestion);

  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [liveMessage, setLiveMessage] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const location = useLocation();

  // Build just the “location” bits (admin2, admin1, country)
  const getLocationDetails = ({ name, admin1_name, admin2_name, country_name, place_type }) => {
    const lowerType = place_type?.toLowerCase() ?? '';

    // Capitals show only the country here
    if (lowerType.includes('capital')) {
      return country_name;
    }

    // Country rows get no “details” (we’ll show type separately)
    if (lowerType === 'country') {
      return '';
    }

    const parts = [];
    [admin2_name, admin1_name, country_name].forEach(val => {
      if (val && val !== name && !parts.includes(val)) {
        parts.push(val);
      }
    });

    return parts.join(', ');
  };

  // place_type always shown separately
  const getPlaceType = suggestion => suggestion.place_type;

  const highlightMatch = (suggestion, query) => {
    if (!query) return suggestion.name;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = suggestion.name.split(regex);
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={index} className="highlight my-span">
          {part}
        </span>
      ) : (
        <span key={index} className="my-span">
          {part}
        </span>
      )
    );
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    setActiveSuggestionIndex(-1);
    setShowSuggestions(searchQuery.trim() !== '' && isFocused);
    if (!isLoading) {
      if (error) {
        setLiveMessage(error);
      } else if (suggestions.length > 0) {
        setLiveMessage(`${suggestions.length} suggestions available`);
      } else if (searchQuery.trim() !== '') {
        setLiveMessage('No suggestions found');
      } else {
        setLiveMessage('');
      }
    } else {
      setLiveMessage('');
    }
  }, [suggestions, searchQuery, error, isLoading, isFocused]);

  useEffect(() => {
    if (activeSuggestionIndex >= 0) {
      const activeElement = document.getElementById(`suggestion-${activeSuggestionIndex}`);
      if (activeElement) {
        activeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeSuggestionIndex]);

  useEffect(() => {
    handleSearchChange({ target: { value: '' } });
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
  }, [location.pathname]);

  const onSuggestionSelect = (suggestion) => {
    handleSuggestionClick(suggestion);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    handleSearchChange({ target: { value: '' } });
  };

  const handleSearchClick = () => {
    if (suggestions.length > 0) {
      onSuggestionSelect(suggestions[0]);
    }
  };

  const handleKeyDownCustom = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev + 1 >= suggestions.length ? 0 : prev + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev - 1 < 0 ? suggestions.length - 1 : prev - 1));
    } else if (event.key === 'Tab' && suggestions.length > 0) {
      event.preventDefault();
      if (event.shiftKey) {
        setActiveSuggestionIndex((prev) => (prev - 1 < 0 ? suggestions.length - 1 : prev - 1));
      } else {
        setActiveSuggestionIndex((prev) => (prev + 1 >= suggestions.length ? 0 : prev + 1));
      }
    } else if (event.key === 'Enter') {
      if (activeSuggestionIndex >= 0 && suggestions.length > 0) {
        event.preventDefault();
        onSuggestionSelect(suggestions[activeSuggestionIndex]);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setActiveSuggestionIndex(-1);
      setShowSuggestions(false);
    } else {
      handleKeyPress(event);
    }
  };

  const activeDescendantId = activeSuggestionIndex >= 0 ? `suggestion-${activeSuggestionIndex}` : undefined;

  return (
    <div className={`searchbar-wrapper ${variant}`}>
      <Form className="searchbar-form" role="search" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        <InputGroup>
          <FormControl
            type="text"
            placeholder="Take me to..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDownCustom}
            onFocus={() => {
              handleFocus();
              setIsFocused(true);
            }}
            onBlur={() => setIsFocused(false)}
            className={`searchbar-input ${variant}`}
            ref={searchInputRef}
            aria-label="Search"
            aria-describedby="searchbar-button"
            aria-activedescendant={activeDescendantId}
          />
          <Button
            variant="outline-secondary"
            onClick={handleSearchClick}
            className={`searchbar-button ${variant}`}
            id="searchbar-button"
            aria-label="Search button"
            type="button"
          >
            <FaMapMarkerAlt aria-hidden="true" className={`loading-icon ${isLoading ? 'spinning' : ''}`} />
          </Button>
        </InputGroup>
        {showSuggestions && (
          <div className="autocomplete-suggestions" role="listbox" ref={dropdownRef}>
            {!isLoading && error && <div className="error-message">{error}</div>}
            {!isLoading && !error && suggestions.length === 0 && searchQuery.trim() !== '' && (
              <div className="no-suggestions-message">No suggestions found</div>
            )}
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                id={`suggestion-${index}`}
                className={`suggestion-item ${index === activeSuggestionIndex ? 'active' : ''}`}
                onMouseDown={() => onSuggestionSelect(suggestion)}
                role="option"
                aria-selected={index === activeSuggestionIndex}
              >
                <div className="suggestion-content">
                  <div className="suggestion-name">
                    {highlightMatch(suggestion, searchQuery)}
                  </div>
                  {getLocationDetails(suggestion) && (
                    <div className="suggestion-details">
                      {getLocationDetails(suggestion)}
                    </div>
                  )}
                  <div className="suggestion-type">
                    {getPlaceType(suggestion)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            border: 0,
          }}
        >
          {liveMessage}
        </div>
      </Form>
    </div>
  );
};

SearchBar.propTypes = {
  navigate: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['navbar', 'landing', 'admin']),
  onSelectSuggestion: PropTypes.func,
};

export default SearchBar;